(() => {
  const header = document.querySelector('[data-oem-header]');
  const menuToggle = document.querySelector('[data-oem-menu-toggle]');
  const menuPanel = document.querySelector('[data-oem-mobile-menu]');
  const submenuToggles = [...document.querySelectorAll('[data-oem-submenu-toggle]')];
  const mobileSubmenuToggles = [...document.querySelectorAll('[data-oem-mobile-submenu-toggle]')];
  const languageSelectors = document.querySelectorAll('[data-oem-language]');
  let menuReturnFocus = null;

  const normalizeNavigationPath = (href) => {
    try {
      const path = new URL(href, window.location.origin).pathname;
      return path.length > 1 ? path.replace(/\/+$/, '') : path;
    } catch (_error) {
      return '';
    }
  };

  const setCurrentNavigationState = () => {
    const currentPath = normalizeNavigationPath(window.location.href);
    const desktopItems = [...document.querySelectorAll('.oem-header__menu-item')];
    const mobileRoot = document.querySelector('.oem-mobile-nav');
    const mobileItems = mobileRoot ? [...mobileRoot.children] : [];

    [...desktopItems, ...mobileItems].forEach((item) => item.classList.remove('is-active-trail'));

    document
      .querySelectorAll('.oem-header__nav a[href], .oem-mobile-nav a[href]')
      .forEach((anchor) => {
        const isCurrent = normalizeNavigationPath(anchor.href) === currentPath;
        if (isCurrent) {
          anchor.setAttribute('aria-current', 'page');
          anchor.closest('.oem-header__menu-item')?.classList.add('is-active-trail');
          mobileItems.find((item) => item.contains(anchor))?.classList.add('is-active-trail');
        } else if (anchor.getAttribute('aria-current') === 'page') {
          anchor.removeAttribute('aria-current');
        }
      });
  };

  const setHeaderState = () => {
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 24);
  };

  const setToggleLabel = (toggle, isOpen) => {
    const label = isOpen ? toggle.dataset.closeLabel : toggle.dataset.openLabel;
    if (label) toggle.setAttribute('aria-label', label);
  };

  const closeMobileSubmenus = ({ except = null } = {}) => {
    mobileSubmenuToggles.forEach((toggle) => {
      if (toggle === except || toggle.getAttribute('aria-expanded') !== 'true') return;
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.setAttribute('aria-expanded', 'false');
      setToggleLabel(toggle, false);
      if (panel) panel.hidden = true;
    });
  };

  const closeMenu = ({ restoreFocus = false } = {}) => {
    if (!menuToggle || !menuPanel) return;
    menuToggle.setAttribute('aria-expanded', 'false');
    setToggleLabel(menuToggle, false);
    menuPanel.hidden = true;
    document.body.classList.remove('oem-menu-open');
    closeMobileSubmenus();
    if (restoreFocus && menuReturnFocus instanceof HTMLElement) menuReturnFocus.focus();
    menuReturnFocus = null;
  };

  const closeSubmenus = ({ except = null, restoreFocus = false } = {}) => {
    submenuToggles.forEach((toggle) => {
      if (toggle === except || toggle.getAttribute('aria-expanded') !== 'true') return;
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.setAttribute('aria-expanded', 'false');
      setToggleLabel(toggle, false);
      if (panel) panel.hidden = true;
      if (restoreFocus) toggle.focus();
    });
    header?.classList.toggle(
      'is-dropdown-open',
      submenuToggles.some((toggle) => toggle.getAttribute('aria-expanded') === 'true')
    );
  };

  if (header) {
    setCurrentNavigationState();
    setHeaderState();
    window.addEventListener('scroll', setHeaderState, { passive: true });
  }

  submenuToggles.forEach((toggle) => {
    toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
      closeSubmenus({ except: toggle });
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.setAttribute('aria-expanded', String(willOpen));
      setToggleLabel(toggle, willOpen);
      if (panel) panel.hidden = !willOpen;
      header.classList.toggle('is-dropdown-open', willOpen);
    });
  });

  mobileSubmenuToggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
      closeMobileSubmenus({ except: toggle });
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.setAttribute('aria-expanded', String(willOpen));
      setToggleLabel(toggle, willOpen);
      if (panel) panel.hidden = !willOpen;
    });
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-oem-nav-item]')) closeSubmenus();
  });

  if (menuToggle && menuPanel) {
    menuToggle.addEventListener('click', () => {
      const willOpen = menuToggle.getAttribute('aria-expanded') !== 'true';
      if (willOpen) menuReturnFocus = document.activeElement;
      menuToggle.setAttribute('aria-expanded', String(willOpen));
      setToggleLabel(menuToggle, willOpen);
      menuPanel.hidden = !willOpen;
      document.body.classList.toggle('oem-menu-open', willOpen);
      if (willOpen) {
        window.requestAnimationFrame(() => menuPanel.querySelector('a, summary, button, select')?.focus());
      }
    });

    menuPanel.addEventListener('click', (event) => {
      if (event.target.closest('a')) closeMenu();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menuToggle.getAttribute('aria-expanded') === 'true') {
        closeMenu({ restoreFocus: true });
      }

      if (event.key === 'Escape' && header?.classList.contains('is-dropdown-open')) {
        closeSubmenus({ restoreFocus: true });
      }

      if (event.key === 'Tab' && menuToggle.getAttribute('aria-expanded') === 'true') {
        const focusable = [
          menuToggle,
          ...menuPanel.querySelectorAll(
            'a[href], summary, button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          ),
        ].filter((element) => element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > 1100 && menuToggle.getAttribute('aria-expanded') === 'true') closeMenu();
      if (window.innerWidth <= 1100) closeSubmenus();
    });
  }

  languageSelectors.forEach((selector) => {
    selector.addEventListener('change', () => selector.form?.submit());
  });

  const publishAnalyticsHook = (eventName, detail = {}) => {
    document.dispatchEvent(
      new CustomEvent('oem:analytics', {
        detail: { event: eventName, ...detail },
      })
    );
  };

  document.querySelectorAll('[data-oem-form-section]').forEach((section) => {
    const formType = section.dataset.oemFormSection;
    const form = section.querySelector('form');
    if (!form) return;

    form.addEventListener(
      'focusin',
      () => publishAnalyticsHook(`${formType}_form_start`, { formType }),
      { once: true }
    );
    form.addEventListener('submit', () =>
      publishAnalyticsHook(`${formType}_form_submit_attempt`, { formType })
    );

    if (section.querySelector(`[data-oem-form-success="${formType}"]`)) {
      publishAnalyticsHook(`${formType}_form_submission_confirmed`, { formType });
    }
  });

  document.addEventListener('click', (event) => {
    const contactLink = event.target.closest('a[href^="mailto:"], a[href^="tel:"]');
    if (contactLink) {
      publishAnalyticsHook('contact_link_click', {
        contactType: contactLink.href.startsWith('mailto:') ? 'email' : 'phone',
      });
    }

    const projectLink = event.target.closest('a[href*="/pages/project-inquiry"]');
    if (projectLink) publishAnalyticsHook('start_project_cta_click');

    const caseStudyLink = event.target.closest('[data-oem-case-study-link]');
    if (caseStudyLink) publishAnalyticsHook('case_study_engagement', { action: 'open' });
  });

  const caseStudyPage = document.querySelector('[data-oem-case-study]');
  if (caseStudyPage) {
    publishAnalyticsHook('case_study_engagement', {
      action: 'view',
      handle: caseStudyPage.dataset.oemCaseStudy,
    });
  }

  const pageMain = document.querySelector('main[data-template]');
  const templateSuffix = pageMain?.dataset.templateSuffix || '';
  if (templateSuffix === 'solutions-overview' || templateSuffix.startsWith('solution-')) {
    publishAnalyticsHook('solution_page_engagement', { action: 'view', template: templateSuffix });
  }
  if (templateSuffix === 'capabilities-overview' || templateSuffix.startsWith('capability-')) {
    publishAnalyticsHook('capability_page_engagement', { action: 'view', template: templateSuffix });
  }

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileHeroQuery = window.matchMedia('(max-width: 749px)');

  const addMediaQueryListener = (query, listener) => {
    if ('addEventListener' in query) {
      query.addEventListener('change', listener);
      return () => query.removeEventListener('change', listener);
    } else {
      query.addListener(listener);
      return () => query.removeListener(listener);
    }
  };

  const initializeHeroVideo = (video) => {
    if (video.dataset.oemHeroVideoInitialized === 'true') return;

    const hero = video.closest('.oem-hero');
    const toggle = hero?.querySelector('[data-oem-hero-video-toggle]');
    if (!hero || !toggle) return;

    video.dataset.oemHeroVideoInitialized = 'true';

    const hasDesktopVideo = video.dataset.hasDesktopVideo === 'true';
    const hasMobileVideo = video.dataset.hasMobileVideo === 'true';
    let userInitiatedPlayback = false;

    const sourceAvailable = () =>
      mobileHeroQuery.matches ? hasMobileVideo || hasDesktopVideo : hasDesktopVideo;

    const updateControl = () => {
      const isPaused = video.paused || video.ended;
      toggle.hidden = !sourceAvailable();
      toggle.dataset.state = isPaused ? 'paused' : 'playing';
      toggle.setAttribute('aria-label', isPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
    };

    const showVideoWhenReady = () => {
      if (!sourceAvailable()) return;
      video.classList.add('is-ready');
    };

    const attemptPlayback = ({ userInitiated = false } = {}) => {
      if (!sourceAvailable() || (reducedMotionQuery.matches && !userInitiated)) {
        updateControl();
        return;
      }

      if (userInitiated) {
        userInitiatedPlayback = true;
        video.classList.add('is-user-playing');
      }

      const playRequest = video.play();
      if (playRequest && typeof playRequest.catch === 'function') {
        playRequest.catch(() => updateControl());
      }
    };

    const configurePlayback = ({ reloadSource = false } = {}) => {
      const isAvailable = sourceAvailable();
      toggle.classList.add('is-initialized');
      toggle.hidden = !isAvailable;

      if (!isAvailable) {
        video.removeAttribute('autoplay');
        video.pause();
        video.classList.remove('is-ready');
        updateControl();
        return;
      }

      if (reloadSource) {
        video.classList.remove('is-ready');
        video.load();
      }

      if (reducedMotionQuery.matches) {
        video.removeAttribute('autoplay');
        video.pause();
        if (!userInitiatedPlayback) video.classList.remove('is-user-playing');
      } else {
        video.setAttribute('autoplay', '');
        attemptPlayback();
      }

      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) showVideoWhenReady();
      updateControl();
    };

    toggle.addEventListener('click', () => {
      if (video.paused || video.ended) {
        attemptPlayback({ userInitiated: true });
      } else {
        video.pause();
      }
    });

    video.addEventListener('loadeddata', showVideoWhenReady);
    video.addEventListener('canplay', showVideoWhenReady);
    video.addEventListener('playing', () => {
      showVideoWhenReady();
      updateControl();
    });
    video.addEventListener('pause', updateControl);
    video.addEventListener('ended', updateControl);
    video.addEventListener('error', () => {
      video.classList.remove('is-ready');
      updateControl();
    });

    const removeMobileListener = addMediaQueryListener(mobileHeroQuery, () =>
      configurePlayback({ reloadSource: true })
    );
    const removeMotionListener = addMediaQueryListener(reducedMotionQuery, () => configurePlayback());

    video.oemHeroVideoCleanup = () => {
      removeMobileListener();
      removeMotionListener();
      video.pause();
    };

    configurePlayback();
  };

  document.querySelectorAll('[data-oem-hero-video]').forEach(initializeHeroVideo);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-oem-hero-video]').forEach(initializeHeroVideo);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-oem-hero-video]').forEach((video) => {
      if (typeof video.oemHeroVideoCleanup === 'function') video.oemHeroVideoCleanup();
    });
  });

  const motionAllowed = !reducedMotionQuery.matches;
  const motionEnabled = document.body.dataset.oemMotion === 'true';
  let revealObserver = null;
  const revealItemsWithin = (root) => [
    ...(root.matches?.('[data-oem-reveal]') ? [root] : []),
    ...root.querySelectorAll('[data-oem-reveal]'),
  ];

  if (motionAllowed && motionEnabled && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('oem-motion-enabled');
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );
  }
  const initializeReveals = (root) => {
    revealItemsWithin(root).forEach((item) => {
      if (item.classList.contains('is-visible')) return;
      if (revealObserver && !reducedMotionQuery.matches) revealObserver.observe(item);
      else item.classList.add('is-visible');
    });
  };
  initializeReveals(document);
  document.addEventListener('shopify:section:load', (event) => initializeReveals(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    revealItemsWithin(event.target).forEach((item) => revealObserver?.unobserve(item));
  });
  addMediaQueryListener(reducedMotionQuery, () => {
    if (!reducedMotionQuery.matches) return;
    revealItemsWithin(document).forEach((item) => {
      item.classList.add('is-visible');
      revealObserver?.unobserve(item);
    });
  });
})();
